import { defineTheme, type DefinedTheme } from '@astryxdesign/core/theme';
import { neutralTheme } from '@astryxdesign/theme-neutral';

const FONT = '"Onest Variable", Onest, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const cache = new Map<string, DefinedTheme>();

/** Page background per scheme. Mirrored by the boot splash in index.html and the browser's theme-color. */
export const BODY_BACKGROUND = { light: '#F1F4F7', dark: '#0B0B0D' } as const;

/**
 * One runtime theme per tenant accent (the documented Theme provider path for
 * themes created from user/business data). Dark by default, light on request
 * (src/features/theme/mode.ts); the accent comes only from the studio configuration.
 */
const channels = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const toHex = (rgb: number[]) => `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

/** WCAG relative luminance, 0 (black) … 1 (white). */
function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Ink that stays readable on the studio's accent: dark on light accents (yellow), white on deep ones (blue). */
const inkOn = (accent: string) => (luminance(accent) > 0.4 ? '#1A1600' : '#FFFFFF');

/** The accent darkened until it reads on a light page (≥ 6:1 on white) — for accent text and icons in the light scheme. */
function readableOnLight(accent: string): string {
  let rgb: number[] = channels(accent);
  for (let i = 0; i < 40 && luminance(toHex(rgb)) > 0.12; i++) rgb = rgb.map((c) => c * 0.94);
  return toHex(rgb);
}

export function tenantTheme(slug: string, accent: string): DefinedTheme {
  const key = `${slug}:${accent}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const theme = defineTheme({
    name: `tenant-${slug}`,
    extends: neutralTheme,
    color: { accent: [accent, accent], neutralStyle: 'neutral' },
    radius: { base: 6, multiplier: 1.4 },
    // Three vehicle classes («Внедорожник») must fit a 360px phone without truncation.
    components: { 'segmented-control-item': { base: { paddingInline: 'var(--spacing-1)' } } },
    tokens: {
      '--font-family-body': FONT,
      '--font-family-heading': FONT,
      // Fills (buttons, switches) carry the exact brand colour in both schemes; accent text and icons get a
      // tone that reads on the page. Never paint text or icons with --color-accent directly.
      '--color-accent': [accent, accent],
      '--color-on-accent': [inkOn(accent), inkOn(accent)],
      '--color-text-accent': [readableOnLight(accent), accent],
      '--color-icon-accent': [readableOnLight(accent), accent],
      '--color-background-body': [BODY_BACKGROUND.light, BODY_BACKGROUND.dark],
      '--color-background-surface': ['#FFFFFF', '#141417'],
      '--color-background-card': ['#FFFFFF', '#17171B'],
      '--color-background-popover': ['#FFFFFF', '#1C1C21'],
    },
  });
  cache.set(key, theme);
  return theme;
}

export const fallbackTheme = tenantTheme('app', '#4690FF');
